import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { ApiError } from '../../../api/client';
import { Money, Qty } from '../../../components/Money';
import { StatusChip } from '../../../components/StatusChip';
import { errorMessage } from '../../../lib/notify';
import { dec } from '../lib/decimal';
import { progress } from '../lib/documents';

/** Detail page header: back link, title + status chip, a subtitle and the primary actions. */
export function DetailHeader({
  back,
  title,
  status,
  subtitle,
  actions,
}: {
  back: { to: string; label: string };
  title: string;
  status?: { status: string; label?: string };
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Box sx={{ mb: 3.5 }}>
      <Button
        component={RouterLink}
        to={back.to}
        size="small"
        startIcon={<ArrowBackRounded fontSize="small" />}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        {back.label}
      </Button>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-end' } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3">{title}</Typography>
            {status && <StatusChip status={status.status} label={status.label} />}
          </Stack>
          {subtitle && (
            <Typography component="div" color="text.secondary" sx={{ mt: 0.75 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

/** A KPI-style summary card on detail pages. */
export function SummaryCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'success' | 'warning' | 'error' | 'primary';
}) {
  return (
    <Card sx={{ p: 2.25, height: '100%' }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h4"
        className="tabular"
        sx={{ mt: 0.5, color: tone ? `${tone}.main` : 'text.primary' }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Card>
  );
}

export function SummaryGrid({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {children}
    </Box>
  );
}

export interface PricedLine {
  id: string;
  description: string;
  sku: string;
  qty: string;
  qtyBase?: string;
  unitSymbol: string;
  unitPrice: string;
  discountAmount: string;
  taxRate: string;
  taxAmount: string;
  lineTotal: string;
  qtyDelivered?: string;
  qtyInvoiced?: string;
}

function Progress({ label, done, total }: { label: string; done: string; total: string }) {
  const pct = progress(done, total);
  return (
    <Box sx={{ minWidth: 92 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" className="tabular" sx={{ fontWeight: 600 }}>
          {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct >= 100 ? 'success' : 'primary'}
        sx={{ height: 5, borderRadius: 3, mt: 0.25 }}
      />
    </Box>
  );
}

/** Lines of an invoice or order; orders also show delivered / invoiced progress. */
export function LinesTable({
  lines,
  withProgress,
}: {
  lines: PricedLine[];
  withProgress?: boolean;
}) {
  // Columns nobody uses only take width from the item names and amounts.
  const anyDiscount = lines.some((l) => dec(l.discountAmount) > 0n);
  const anyTax = lines.some((l) => dec(l.taxAmount) > 0n);
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
              Price
            </TableCell>
            {anyDiscount && (
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                Discount
              </TableCell>
            )}
            {anyTax && (
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                Tax
              </TableCell>
            )}
            {withProgress && (
              <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, width: 220 }}>
                Progress
              </TableCell>
            )}
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((l) => (
            <TableRow key={l.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell sx={{ minWidth: { sm: 200 } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {l.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {l.sku}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Qty value={l.qty} unit={l.unitSymbol} />
              </TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                <Money value={l.unitPrice} />
              </TableCell>
              {anyDiscount && (
                <TableCell
                  align="right"
                  sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                >
                  {dec(l.discountAmount) > 0n ? <Money value={l.discountAmount} /> : '—'}
                </TableCell>
              )}
              {anyTax && (
                <TableCell
                  align="right"
                  sx={{ display: { xs: 'none', md: 'table-cell' }, color: 'text.secondary' }}
                >
                  {dec(l.taxAmount) > 0n ? (
                    <>
                      <Money value={l.taxAmount} />
                      <Typography variant="caption" component="div">
                        {Number(l.taxRate)}%
                      </Typography>
                    </>
                  ) : (
                    '—'
                  )}
                </TableCell>
              )}
              {withProgress && (
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Stack direction="row" spacing={1.5}>
                    <Progress
                      label="Delivered"
                      done={l.qtyDelivered ?? '0'}
                      total={l.qtyBase ?? l.qty}
                    />
                    <Progress
                      label="Invoiced"
                      done={l.qtyInvoiced ?? '0'}
                      total={l.qtyBase ?? l.qty}
                    />
                  </Stack>
                </TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Money value={l.lineTotal} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Totals block under the lines (right-aligned). */
export function TotalsBlock({
  doc,
  extra,
}: {
  doc: {
    subtotal: string;
    discountTotal: string;
    taxTotal: string;
    total: string;
    pricesIncludeTax: boolean;
  };
  extra?: ReactNode;
}) {
  const row = (label: string, value: ReactNode, strong?: boolean) => (
    <Stack direction="row" sx={{ justifyContent: 'space-between', py: 0.5 }}>
      <Typography
        color={strong ? 'text.primary' : 'text.secondary'}
        sx={{ fontWeight: strong ? 700 : 400 }}
      >
        {label}
      </Typography>
      <Typography className="tabular" sx={{ fontWeight: strong ? 700 : 550 }}>
        {value}
      </Typography>
    </Stack>
  );
  return (
    <Box sx={{ ml: 'auto', width: { xs: '100%', sm: 340 }, px: 2.5, py: 2 }}>
      {row('Subtotal', <Money value={doc.subtotal} />)}
      {dec(doc.discountTotal) > 0n &&
        row(
          'Discounts',
          <Box component="span" sx={{ color: 'success.main' }}>
            −<Money value={doc.discountTotal} />
          </Box>,
        )}
      {dec(doc.taxTotal) > 0n &&
        row(doc.pricesIncludeTax ? 'Tax (included)' : 'Tax', <Money value={doc.taxTotal} />)}
      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1, pt: 1 }}>
        {row('Total', <Money value={doc.total} />, true)}
      </Box>
      {extra}
    </Box>
  );
}

/** Skeleton shaped like a detail page (header, four cards, a lines card). */
export function DetailSkeleton() {
  return (
    <Box aria-busy="true">
      <Skeleton width={90} height={28} />
      <Skeleton width={260} height={48} sx={{ mb: 3 }} />
      <SummaryGrid>
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} sx={{ p: 2.25 }}>
            <Skeleton width="50%" />
            <Skeleton width="70%" height={36} />
          </Card>
        ))}
      </SummaryGrid>
      <Card sx={{ p: 2.5 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={44} />
        ))}
      </Card>
    </Box>
  );
}

/** Error state: message, request id and a retry. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <Card sx={{ textAlign: 'center', py: 8, px: 3 }}>
      <ErrorOutlineRounded
        sx={{ fontSize: 44, color: notFound ? 'text.disabled' : 'error.main' }}
      />
      <Typography variant="h5" sx={{ mt: 1.5 }}>
        {notFound ? 'Not found' : 'Could not load this'}
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 0.75 }}>
        {notFound ? 'It may have been removed, or the link is wrong.' : errorMessage(error)}
      </Typography>
      {requestId && (
        <Typography variant="caption" color="text.disabled" component="div" sx={{ mt: 1 }}>
          Request id {requestId}
        </Typography>
      )}
      {onRetry && !notFound && (
        <Button variant="outlined" sx={{ mt: 2.5 }} onClick={onRetry}>
          Try again
        </Button>
      )}
    </Card>
  );
}

/** Confirmation that needs a written reason (cancel, close, reverse). */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  reasonRequired = true,
  danger = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  reasonRequired?: boolean;
  danger?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 3;
  const invalid = (reasonRequired && trimmed.length < 3) || tooShort;
  const close = () => {
    if (busy) return;
    setReason('');
    setError(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          setBusy(true);
          setError(null);
          onConfirm(trimmed)
            .then(() => {
              setReason('');
              onClose();
            })
            .catch((err: unknown) => setError(errorMessage(err)))
            .finally(() => setBusy(false));
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" component="div">
            {body}
          </Typography>
          <TextField
            label={reasonRequired ? 'Reason' : 'Reason (optional)'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            autoFocus
            multiline
            minRows={2}
            sx={{ mt: 2.5 }}
            error={tooShort}
            helperText={tooShort ? 'At least 3 characters' : 'Kept in the activity log'}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={busy}>
            Back
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={danger ? 'error' : 'primary'}
            loading={busy}
            disabled={invalid}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Small "label: value" pair for info cards. */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 550, textAlign: 'right' }}>
        {children}
      </Typography>
    </Stack>
  );
}
